// src/components/select/DashboardGranularity.tsx

import { FormControl, MenuItem, Select, Stack, Typography } from '@mui/material'

const DEFAULT_GRANULARITY = [
  { label: 'Letzter voller Monat', value: 'lastFullMonth' },
  { label: 'Letzten 30 Tage', value: 'lastThirtyDays' },
  { label: 'Letzten 2 Monate', value: 'lastTwoMonths' },
  { label: 'Letzten 6 Monate', value: 'lastSixMonths' },
  { label: 'Dieses Jahr (Januar bis heute)', value: 'thisYear' },
  { label: 'Letztes Jahr', value: 'lastYear' },
  { label: 'Letzten 2 Jahre', value: 'lastTwoYears' },
  { label: 'Alle verfügbaren Jahre', value: 'allYears' },
]

const shortDateFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})

const monthYearFmt = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
})

const dateFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const formatDateShortDE = d => shortDateFmt.format(d)
const formatDateDE = d => dateFmt.format(d)
const formatMonthYearDE = d => monthYearFmt.format(d)
const monthFmt = new Intl.DateTimeFormat('de-DE', { month: 'long' })
const formatMonthDE = d => monthFmt.format(d)

const addMonths = (d, m) => {
  const nd = new Date(d)
  const day = nd.getDate()
  nd.setMonth(nd.getMonth() + m)

  if (nd.getDate() < day) nd.setDate(0)
  return nd
}

const getRangeText = (value, now = new Date(), earliestYear?: number) => {
  switch (value) {
    case 'lastFullMonth': {
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastMonth = addMonths(firstOfThisMonth, -1)
      return formatMonthYearDE(lastMonth)
    }
    case 'lastThirtyDays': {
      const end = new Date(now)
      const start = new Date(now)
      start.setDate(start.getDate() - 30)
      return `${formatDateDE(start)} bis ${formatDateDE(end)}`
    }
    case 'lastTwoMonths': {
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastMonth = addMonths(firstOfThisMonth, -1)
      const twoMonthsAgo = addMonths(firstOfThisMonth, -2)
      if (lastMonth.getFullYear() === twoMonthsAgo.getFullYear())
        return `${formatMonthDE(twoMonthsAgo)} und ${formatMonthDE(lastMonth)} ${lastMonth.getFullYear()}`

      return `${formatMonthYearDE(twoMonthsAgo)} und ${formatMonthYearDE(lastMonth)}`
    }
    case 'lastSixMonths': {
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const endMonth = addMonths(firstOfThisMonth, -1)
      const startMonth = addMonths(firstOfThisMonth, -6)
      if (startMonth.getFullYear() === endMonth.getFullYear())
        return `${formatMonthDE(startMonth)} bis ${formatMonthDE(endMonth)} ${endMonth.getFullYear()}`

      return `${formatMonthYearDE(startMonth)} bis ${formatMonthYearDE(endMonth)}`
    }
    case 'thisYear': {
      const y = now.getFullYear()
      const end = new Date(now)
      return `${y} (bis ${formatDateShortDE(end)})`
    }
    case 'lastYear': {
      const y = now.getFullYear() - 1
      return String(y)
    }

    case 'lastTwoYears': {
      const y = now.getFullYear()
      return `${y - 2} und ${y - 1}`
    }

    case 'allYears': {
      const endYear = now.getFullYear()
      const startYear = Math.min(earliestYear ?? 2018, endYear)

      return `${startYear} bis ${endYear}`
    }
    default:
      return ''
  }
}

type GranularityItemProps = {
  item: {
    label: string
    value: string
  }
  onChange: (event: { target: { value: string } }) => void
}

const GranularityItem = ({ item, onChange }: GranularityItemProps) => (
  <MenuItem
    value={item.value}
    onClick={() => onChange({ target: { value: item.value } })}
  >
    {item.label}
  </MenuItem>
)

type Props = {
  data?: { label: string; value: string }[]
  onChange: (event: { target: { value: string } }) => void
  value: string
  earliestYear?: number
}

export const DashboardGranularity = ({
  data = DEFAULT_GRANULARITY,
  onChange,
  value,
  earliestYear,
  ...rest
}: Props) => {
  const renderValue = selected => {
    const found = (data || []).find(d => d.value === selected)
    if (!found) return <Typography>Bitte wählen ...</Typography>

    const rangeText = getRangeText(found.value, new Date(), earliestYear)
    return (
      <Stack sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
        <Typography variant={'body1'}>{found.label}</Typography>
        {rangeText ? (
          <Typography
            variant={'body2'}
            sx={{ fontStyle: 'italic', color: 'text.secondary' }}
          >
            {rangeText}
          </Typography>
        ) : null}
      </Stack>
    )
  }

  return (
    <FormControl size={'small'} {...rest}>
      <Select renderValue={renderValue} value={value ?? ''}>
        {data.map(item => (
          <GranularityItem item={item} key={item.value} onChange={onChange} />
        ))}
      </Select>
    </FormControl>
  )
}
